import { useCallback } from 'react';
import { toast } from 'react-toastify';
import { useFirstErrorFocus } from './useFirstErrorFocus';

/**
 * Declarative schema-based form validation hook.
 *
 * Schema formatı:
 * {
 *   fieldName: {
 *     required:             true | false,         // Zorunlu alan
 *     trim:                 true | false,          // Trim sonrası boş kontrolü (required ile kullanılır)
 *     message:              'Hata mesajı',         // Zorunlu alan hatası
 *     minLength:            number,                // Minimum karakter (trim sonrası)
 *     minLengthMessage:     'Hata mesajı',
 *     maxLength:            number,                // Maksimum karakter
 *     maxLengthMessage:     'Hata mesajı',
 *     min:                  number,                // Minimum sayısal değer
 *     max:                  number,                // Maksimum sayısal değer
 *     rangeMessage:         'Hata mesajı',
 *     minWords:             number,                // Minimum kelime sayısı (ad soyad vs.)
 *     minWordsMessage:      'Hata mesajı',
 *     regex:                RegExp,                // Regex testi
 *     regexMessage:         'Hata mesajı',
 *     validator:            (value) => bool | { isValid, error }, // Fonksiyon bazlı doğrulama
 *     validatorMessage:     'Hata mesajı',
 *     custom:               (value, formData) => string | null,  // Tamamen özel kontrol — string ise hata
 *     conditionalRequired:  (formData) => bool,   // Dinamik zorunluluk
 *     when:                 (formData) => bool,    // false ise bu field atlanır
 *   }
 * }
 *
 * @param {Object}   schema   — Validation kuralları
 * @param {Object}   formData — Form state'i
 * @param {Function} setErrors — Hata setter
 * @param {Object}   [options]
 * @param {string}   [options.toastMessage] — Özelleştirilmiş hata toast'ı
 * @returns {{ validate: () => Object }}
 */
export const useFormValidation = (schema, formData, setErrors, options = {}) => {
    const focusFirstError = useFirstErrorFocus();
    const { toastMessage = 'Lütfen formdaki hataları düzeltin.' } = options;

    const validate = useCallback(() => {
        const errors = {};

        for (const [fieldName, rules] of Object.entries(schema)) {
            // when koşulu false ise field'ı atla
            if (rules.when && !rules.when(formData)) continue;

            const rawValue = formData[fieldName];
            const value = typeof rawValue === 'string' ? rawValue : rawValue;
            const trimmed = typeof rawValue === 'string' ? rawValue.trim() : rawValue;

            // --- required / conditionalRequired ---
            const isRequired = rules.required || (rules.conditionalRequired && rules.conditionalRequired(formData));

            if (isRequired) {
                const isEmpty =
                    value === null ||
                    value === undefined ||
                    value === '' ||
                    (typeof value === 'string' && (rules.trim !== false) && !trimmed) ||
                    (Array.isArray(value) && (value.length === 0 || !value[0]));

                if (isEmpty) {
                    errors[fieldName] = rules.message || `${fieldName} zorunludur`;
                    continue;
                }
            }

            // Eğer alan boşsa ve zorunlu değilse geri kalan kontrollerden geç
            const isBlank =
                value === null || value === undefined || value === '' ||
                (typeof value === 'string' && !trimmed);
            if (isBlank && !isRequired) continue;

            // --- minLength ---
            if (rules.minLength && typeof trimmed === 'string' && trimmed.length < rules.minLength) {
                errors[fieldName] = rules.minLengthMessage || `En az ${rules.minLength} karakter olmalıdır`;
                continue;
            }

            // --- maxLength ---
            if (rules.maxLength && typeof trimmed === 'string' && trimmed.length > rules.maxLength) {
                errors[fieldName] = rules.maxLengthMessage || `En fazla ${rules.maxLength} karakter olmalıdır`;
                continue;
            }

            // --- minWords ---
            if (rules.minWords && typeof trimmed === 'string') {
                const wordCount = trimmed.split(/\s+/).filter(Boolean).length;
                if (wordCount < rules.minWords) {
                    errors[fieldName] = rules.minWordsMessage || `En az ${rules.minWords} kelime olmalıdır`;
                    continue;
                }
            }

            // --- min / max (sayısal) ---
            if (rules.min !== undefined || rules.max !== undefined) {
                const num = typeof value === 'string' ? parseFloat(String(value).replace(',', '.')) : Number(value);
                if (!isNaN(num)) {
                    if (rules.min !== undefined && num < rules.min) {
                        errors[fieldName] = rules.rangeMessage || `${rules.min} ile ${rules.max ?? '∞'} arasında olmalıdır`;
                        continue;
                    }
                    if (rules.max !== undefined && num > rules.max) {
                        errors[fieldName] = rules.rangeMessage || `${rules.min ?? 0} ile ${rules.max} arasında olmalıdır`;
                        continue;
                    }
                }
            }

            // --- regex ---
            if (rules.regex && typeof trimmed === 'string' && !rules.regex.test(trimmed)) {
                errors[fieldName] = rules.regexMessage || rules.message || `${fieldName} formatı geçersiz`;
                continue;
            }

            // --- validator (fonksiyon) ---
            if (rules.validator) {
                const result = rules.validator(value);
                // Fonksiyon bool | { isValid, error } dönebilir
                if (typeof result === 'boolean' && !result) {
                    errors[fieldName] = rules.validatorMessage || rules.message || `${fieldName} geçersiz`;
                    continue;
                }
                if (typeof result === 'object' && result !== null && !result.isValid) {
                    errors[fieldName] = result.error || rules.validatorMessage || rules.message || `${fieldName} geçersiz`;
                    continue;
                }
            }

            // --- custom ---
            if (rules.custom) {
                const customError = rules.custom(value, formData);
                if (customError) {
                    errors[fieldName] = customError;
                    continue;
                }
            }
        }

        setErrors(errors);

        if (Object.keys(errors).length > 0) {
            toast.error(toastMessage);
            focusFirstError(errors);
        }

        return errors;
    }, [schema, formData, setErrors, toastMessage, focusFirstError]);

    return { validate };
};
